import { useEffect } from "react";
import { pushPageScroll, pushPercentScrolled } from "../../utils/gtm";

const MILESTONES = [25, 50, 75, 90];

/**
 * Dispara `page_scroll` no primeiro scroll e `percent_scrolled` em cada marco
 * de profundidade atingido, no máximo uma vez por marco.
 *
 * Páginas sem área rolável são ignoradas nos marcos: sem isso, a página de
 * cadastro concluído — mais curta que a viewport — reportaria 90% de leitura
 * sem que o usuário rolasse nada.
 *
 * `resetKey` reinicia a medição; passe o `pathname` para que cada rota da SPA
 * seja medida do zero.
 */
export function useScrollTracking(resetKey: string): void {
  useEffect(() => {
    let firstScrollSent = false;
    let pending = MILESTONES.slice();
    let frame = 0;

    const measure = (): void => {
      frame = 0;

      if (!firstScrollSent) {
        firstScrollSent = true;
        pushPageScroll();
      }

      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const percent = (window.scrollY / scrollable) * 100;
      const reached = pending.filter((milestone) => percent >= milestone);
      if (!reached.length) return;

      pending = pending.filter((milestone) => percent < milestone);
      reached.forEach(pushPercentScrolled);
    };

    const handleScroll = (): void => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [resetKey]);
}
